// Copyright (c) StrangeBee 2024

import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import useConfigList from '@Hooks/useConfigList';
import { AlertOutput, AuditOutput, ObjectTypeV1 } from 'thehive-sdk';
import { AuditList } from '@Organisms/Audit/AuditList/AuditList';
import { ICONS } from '@Constants/iconsApp';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Alert/selector';
import usePageTitle from '@Hooks/usePageTitle';
import { useLocalMessage } from '@Helpers/locale';

const AlertHistoryList = (): ReactElement => {
    const i = useLocalMessage();
    const { alertId } = useParams<{ alertId: string }>();

    const config = useConfigList<AuditOutput['base']>({
        selector: {
            head: [{ name: 'listAuditFromObject', idOrName: alertId, id: true }],
            name: `list-audit-from-alert-${alertId}`,
            sort: ['-_createdAt'],
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.ALERT_HISTORY,
        objectType: ObjectTypeV1.AUDIT,
        streamRootId: alertId,
        streamObjectType: ObjectTypeV1.ALERT,
    });

    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
        manual: true,
    });

    usePageTitle(i('page-title.alerts.history', { title: alertData?.title || '' }));

    return (
        <THLayout>
            <THLayout.Content>
                <AuditList config={config} />
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        icon: <ICONS.ReadOutlined />,
        label: 'alert.layout.nav-bar.history',
    },
    path: RouteBuilder.compute('alerts.history'),
    content: <AlertHistoryList />,
    exact: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.history' },
};
